// 오프라인 렌더 — 기계 판정이 **실제 예약 코드**를 재게 만드는 자리
//
// 판정의 값어치가 여기 걸린다. 판정용으로 따로 만든 파형을 판정하면 아무것도 검증되지
// 않는다. 그래서 이 파일은 파형을 그리지 않고, 화면이 실제로 부르는 signal.ts 의
// scheduleSignal 을 **그대로** 부른 뒤 브라우저의 Web Audio 가 렌더한 결과를 돌려준다.
//
// 성립하는 이유는 signal.ts 가 브라우저 타입이 아니라 자기가 정한 최소 인터페이스에
// 기대기 때문이다. OfflineAudioContext 도 그 인터페이스를 만족하고, 아직 렌더를
// 시작하지 않아 상태가 suspended 인 것도 통과한다 — scheduleSignal 이 막는 상태는
// 다시 살릴 수 없는 closed 하나뿐이다.
//
// **이것은 시험용 화면 전용 코드가 아니다.** 프로브의 존재 이유가 「늦게 알면 비싼 것을
// 가장 이른 자리에서 재는 것」이고, 이 기기의 오디오 스택이 계약대로 된 파형을 내는지는
// 정확히 그런 종류다. 기기마다 다를 수 있고, 다르면 실기기에서만 드러난다.

import { SIGNAL_PROFILE, signalWindowSec, type SignalProfile } from './profile.ts'
import { scheduleSignal, type AudioContextLike } from './signal.ts'

/** 1760 Hz 를 넉넉히 담고 온셋을 0.02밀리초 격자로 재는 표본율. */
export const RENDER_SAMPLE_RATE = 48_000

/** 구간 앞에 두는 여백. 첫 표지의 램프가 버퍼 밖으로 잘려 나가지 않게 한다. */
export const RENDER_LEAD_SEC = 0.5

/** 구간 뒤에 두는 여백. 종점의 꼬리가 온전히 들어와야 길이를 잴 수 있다. */
export const RENDER_TAIL_SEC = 0.2

export interface RenderedSignal {
  readonly samples: Float32Array
  readonly sampleRate: number
  /** 이 버퍼 안에서 개시(T=0)가 놓인 시각. 판정이 종점 온셋을 견주는 기준이다. */
  readonly startAtSec: number
}

interface RenderedBuffer {
  getChannelData(channel: number): Float32Array
}

interface OfflineContextLike extends AudioContextLike {
  startRendering(): Promise<RenderedBuffer>
}

type OfflineContextCtor = new (
  channels: number,
  length: number,
  sampleRate: number,
) => OfflineContextLike

export async function renderSignalOffline(
  options: { profile?: SignalProfile; sampleRate?: number } = {},
): Promise<RenderedSignal> {
  const profile = options.profile ?? SIGNAL_PROFILE
  const sampleRate = options.sampleRate ?? RENDER_SAMPLE_RATE

  const Ctor = (globalThis as { OfflineAudioContext?: OfflineContextCtor }).OfflineAudioContext
  if (Ctor === undefined) {
    throw new Error('이 브라우저에 OfflineAudioContext 가 없다. 렌더 판정을 할 수 없다.')
  }

  const startAtSec = RENDER_LEAD_SEC + signalWindowSec(profile)
  const tailSec = profile.events.reduce(
    (longest, event) => Math.max(longest, event.onsetSec + event.durationSec + event.releaseSec),
    0,
  )
  const lengthSec = startAtSec + tailSec + RENDER_TAIL_SEC

  const ctx = new Ctor(1, Math.ceil(lengthSec * sampleRate), sampleRate)
  const scheduled = scheduleSignal(ctx, { startAtSec, profile })
  if (scheduled.kind === 'blocked') throw new Error(scheduled.reason)
  if (scheduled.kind === 'too-late') {
    throw new Error('렌더 컨텍스트가 예약을 늦었다고 판단했다 — 여백 계산이 틀렸다.')
  }

  const buffer = await ctx.startRendering()
  return { samples: buffer.getChannelData(0), sampleRate, startAtSec }
}
